import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Device, DevicePresence } from "@gooner/shared";
import { deviceEmoji } from "../lib/deviceEmoji";
import { cx } from "../ui/primitives";
import { MASK } from "../../lib/replayMask";

/**
 * "Send to" — pick a screen for this view, from the view's side of the relationship.
 *
 * The desktop's ONE assignment gesture. Driving from the screen's side — "what is on
 * screen 2?" — is the remote's job (its rail, the What's Playing sheet); the desktop rail
 * that used to offer it here (drag onto a screen card, or its `Showing:` select) is gone.
 * What remains is the gesture that belongs to the library: "put THIS view somewhere",
 * which is how you think about it when the view is what you are looking at.
 *
 * It lists only LIVE devices, for the same reason the remote's rail does: a screen that is
 * not on is not somewhere you can send anything, and offering it would be offering a no-op.
 *
 * Portalled, because the card it belongs to lives inside a grid with `overflow-hidden` on
 * every cell — rendered in place it would be clipped to the card it is escaping.
 */
export function SendToPopover({
  anchor,
  devices,
  presence,
  currentViewId,
  onPick,
  onClose,
}: {
  anchor: HTMLElement;
  /** Already filtered to the live ones by the caller (liveDevices), same as the remote. */
  devices: Device[];
  presence: Record<string, DevicePresence>;
  /** Marked as where it already is, rather than offered as a change that does nothing. */
  currentViewId: string;
  onPick: (deviceId: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos] = useState(() => {
    const r = anchor.getBoundingClientRect();
    return { top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) };
  });

  // Dismiss on an outside click or Escape. Both, because either alone leaves one way to
  // get stuck: a menu with no keyboard exit, or one a mouse cannot put away.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label="Send this view to a screen"
      style={{ top: pos.top, right: pos.right }}
      className="fixed z-50 w-56 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-1.5 shadow-2xl"
    >
      {devices.length === 0 ? (
        <p className="px-2 py-3 text-center text-[11px] text-zinc-500">
          No screens connected. Connect one from the remote on your phone.
        </p>
      ) : (
        devices.map((d) => {
          const here = d.showing?.viewId === currentViewId;
          return (
            <button
              key={d.id}
              type="button"
              role="menuitem"
              disabled={here}
              onClick={() => onPick(d.id)}
              className={cx(
                "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors",
                here
                  ? "cursor-default text-zinc-500"
                  : "text-zinc-200 hover:bg-[var(--color-panel-2)]",
              )}
            >
              <span aria-hidden className="text-base leading-none">
                {deviceEmoji(d.kind)}
              </span>
              {/* Device names are user-renamable: masked in replay. */}
              <span className="min-w-0 flex-1 truncate" {...MASK}>{d.name}</span>
              {here ? (
                <span className="shrink-0 text-[10px] uppercase tracking-wide">on</span>
              ) : (
                <span
                  aria-hidden
                  className={cx(
                    "h-1.5 w-1.5 shrink-0 rounded-full",
                    presence[d.id]?.online ? "bg-emerald-400" : "bg-zinc-600",
                  )}
                />
              )}
            </button>
          );
        })
      )}
    </div>,
    document.body,
  );
}
